import { useTheme } from '@mui/material/styles';
import { HelpOutlineOutlined } from '@mui/icons-material';

import locales from '../../../../../../../constants/locales';
import DetailsList from '../../../../../../common/details-list';
import { nortadaLogger } from '../../../../../../common/logger';
import {
  NameAndMsisdnType,
  NameAndBankAccountType,
  MsisdnAndDateOfBirthType,
  BvnAndNameAndDateOfBirthType
} from '../../../../../../../api/interfaces';
import { detailsValueFormatter } from '../../../../../../../utils/details';
import DetailsFactory from '../../../../../../common/details-list/detailsFactory';
import { convertQueryTypeToRedableFormat } from '../../../../../../common/table/utils/operationTypeFormatters';

import { OperationUnknown } from './interfaces';

interface Props {
  operation: OperationUnknown;
}

const UnknownOperation = ({ operation }: Props) => {
  const theme = useTheme();
  const { details } = operation;

  const error =
    details.errorCode && details.errorMessage
      ? { errorCode: details.errorCode, errorMessage: details.errorMessage }
      : undefined;

  const detailsDependingOnTheQueryType = () => {
    const detailsQueryType = details.query.type;
    const convertedDetailsQueryType = convertQueryTypeToRedableFormat(detailsQueryType);

    switch (details.query.type) {
      case 'NAME_AND_MSISDN': {
        const detailsQuery = details.query as NameAndMsisdnType;

        return (
          <DetailsList
            items={[
              {
                label: locales.operation.label.type,
                value: convertedDetailsQueryType
              },
              { label: locales.operation.label.name, value: detailsQuery.name },
              {
                label: locales.operation.label.msisdn,
                value: detailsValueFormatter(detailsQuery.msisdn)
              }
            ]}
          />
        );
      }
      case 'NAME_AND_BANK_ACCOUNT': {
        const detailsQuery = details.query as NameAndBankAccountType;

        return (
          <DetailsList
            items={[
              {
                label: locales.operation.label.type,
                value: convertedDetailsQueryType
              },
              { label: locales.operation.label.name, value: detailsQuery.name },
              { label: locales.operation.label.bankCode, value: detailsQuery.bankCode },
              {
                label: locales.operation.label.bankAccountNumber,
                value: detailsQuery.bankAccountNumber
              }
            ]}
          />
        );
      }
      case 'MSISDN_AND_DATE_OF_BIRTH': {
        const detailsQuery = details.query as MsisdnAndDateOfBirthType;

        return (
          <DetailsList
            items={[
              {
                label: locales.operation.label.type,
                value: convertedDetailsQueryType
              },
              {
                label: locales.operation.label.msisdn,
                value: detailsValueFormatter(detailsQuery.msisdn)
              },
              {
                label: locales.operation.label.dateOfBirth,
                value: detailsQuery.dateOfBirth
              }
            ]}
          />
        );
      }
      case 'BVN_AND_NAME_AND_DATE_OF_BIRTH': {
        const detailsQuery = details.query as BvnAndNameAndDateOfBirthType;

        return (
          <DetailsList
            items={[
              {
                label: locales.operation.label.type,
                value: convertedDetailsQueryType
              },
              { label: locales.operation.label.name, value: detailsQuery.name },
              { label: locales.operation.label.bvn, value: detailsQuery.bvn },
              {
                label: locales.operation.label.dateOfBirth,
                value: detailsQuery.dateOfBirth
              }
            ]}
          />
        );
      }
      default: {
        nortadaLogger.critical(`${detailsQueryType} type is not defined type`);
        return null;
      }
    }
  };

  return (
    <DetailsFactory
      header={{
        icon: { component: HelpOutlineOutlined, color: theme.palette.grey['700'] },
        id: operation.id,
        date: operation.date,
        status: operation.status
      }}
      error={error}
      operationContent={detailsDependingOnTheQueryType()}
    />
  );
};

export default UnknownOperation;
